import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexReactClient } from "convex/react";
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router";
import "./index.css";
import { installTheme } from "./theme";
import Landing from "./Landing";
import Health from "./Health";
import AuthPage from "./auth/AuthPage";
import App from "./App";
import CalendarPage from "./Calendar";
import ListDetail from "./ListDetail";
import InvitePage from "./InvitePage";
import Settings from "./Settings";
import PrivacyPolicy from "./PrivacyPolicy";
import Support from "./Support";

/**
 * Old reset links (#174): every email sent to date points at the site
 * ROOT with ?code=… attached — a shape nothing consumed, so the link
 * landed on the marketing page and the code had to be retyped by hand.
 * Bounce straight to the reset form with the code; everyone else gets
 * the landing page, exactly as before.
 */
function RootRoute() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  return code ? (
    <Navigate replace to={`/reset?code=${encodeURIComponent(code)}`} />
  ) : (
    <Landing />
  );
}

const address = import.meta.env.VITE_CONVEX_URL;
if (!address) {
  throw new Error("VITE_CONVEX_URL is not set — copy .env.example to .env.local");
}
// A trailing slash yields wss://host//api/... sync paths, which 404 forever
// while the client silently retries ("Connecting…" never resolves).
const client = new ConvexReactClient(address.replace(/\/+$/, ""));

installTheme();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ConvexAuthProvider client={client}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          {/* Diagnostics seam (#178): the deploy gate reads this route;
              the contract is documented in Health.tsx. */}
          <Route path="/health" element={<Health />} />
          {/* The key gives each auth route its own component instance, so
              switching modes client-side starts from clean form state. */}
          <Route path="/signin" element={<AuthPage key="signin" />} />
          <Route path="/signup" element={<AuthPage key="signup" />} />
          <Route path="/reset" element={<AuthPage key="reset" />} />
          {/* Email-verification gate (#126): sign-up/sign-in on an
              unverified account lands here; ?email= means a code was
              just sent, ?invite= keeps the join alive through it. */}
          <Route path="/verify" element={<AuthPage key="verify" />} />
          <Route path="/app" element={<App />} />
          {/* The web calendar (#216): the mobile pair's month grid +
              day panel; entered from the home header. */}
          <Route path="/app/calendar" element={<CalendarPage />} />
          <Route path="/app/settings" element={<Settings />} />
          <Route path="/app/list/:listId" element={<ListDetail />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          {/* Store-metadata pages (ticket #25): static, backend-free, and
              reachable signed-out so reviewers can see them. */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />
        </Routes>
      </BrowserRouter>
    </ConvexAuthProvider>
  </StrictMode>,
);
